import { ConnectError } from '@bufbuild/connect';
import { useIntl } from 'react-intl';

import { MIN_PASSWORD_LENGTH, UserBadRequestError } from '@/shared/config';
import { signInErrorMessages } from '@/shared/i18n';
import { safeJSONParse } from '@/shared/lib';

import { i18n, MFAErrorContext } from '../config';

/**
 * Resolves the inline message under the code boxes. The backend reports every
 * MFA failure as a UserBadRequestError code, and the same code reads
 * differently while enrolling than while signing in, hence the context.
 */
export const useMFAErrorMessage = (context: MFAErrorContext) => {
  const { formatMessage } = useIntl();

  return (error: unknown): string => {
    const { code } = safeJSONParse(ConnectError.from(error).rawMessage);

    switch (code) {
      case UserBadRequestError.WRONG_CODE:
        return formatMessage(i18n.wrongCodeMessages[context]);

      case UserBadRequestError.CODE_RETRIES_LIMIT:
        return formatMessage({
          id: 'mfa.error.retriesLimit',
          defaultMessage:
            'Too many incorrect codes. Sign in again to start a new attempt.',
        });

      case UserBadRequestError.CODE_IS_EXPIRED:
        return formatMessage({
          id: 'mfa.error.expired',
          defaultMessage: 'This attempt has expired. Sign in again.',
        });

      case UserBadRequestError.MFA_ENFORCED:
        return formatMessage({
          id: 'mfa.error.enforced',
          defaultMessage:
            'Two-step verification is required for your account and cannot be turned off.',
        });

      case UserBadRequestError.MFA_ADMIN_TARGET:
        return formatMessage({
          id: 'mfa.error.adminTarget',
          defaultMessage:
            'Two-step verification for this account can only be changed by Diagnocat support.',
        });

      case UserBadRequestError.MFA_DISABLE_REQUIRES_LONGER_PASSWORD:
        return formatMessage(
          {
            id: 'mfa.error.passwordTooShort',
            defaultMessage:
              'Your password is shorter than {minLength} characters. To turn off two-step verification, first set a password of at least {minLength} characters through password recovery.',
          },
          { minLength: MIN_PASSWORD_LENGTH },
        );

      case UserBadRequestError.PASSWORD_RESET_REQUESTED:
        return formatMessage(
          signInErrorMessages[UserBadRequestError.PASSWORD_RESET_REQUESTED],
        );

      case UserBadRequestError.MFA_PENDING_MISSING:
      case UserBadRequestError.MFA_NOT_ENROLLED:
        return formatMessage({
          id: 'mfa.error.setupLost',
          defaultMessage: 'The setup was interrupted. Start it again.',
        });

      default:
        return formatMessage({
          id: 'mfa.error.unknown',
          defaultMessage: 'Something went wrong. Please try again.',
        });
    }
  };
};
